package com.schoolconnect.app.feature.academicyears

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import com.schoolconnect.app.designsystem.AppDialogActionStyle
import com.schoolconnect.app.designsystem.AppDialogDateField
import com.schoolconnect.app.designsystem.AppDialogDivider
import com.schoolconnect.app.designsystem.appDateInputComparable
import com.schoolconnect.app.designsystem.AppDialogFieldColumn
import com.schoolconnect.app.designsystem.AppDialogFormGrid
import com.schoolconnect.app.designsystem.AppDialogFooter
import com.schoolconnect.app.designsystem.AppDialogFooterAction
import com.schoolconnect.app.designsystem.AppDialogHeader
import com.schoolconnect.app.designsystem.AppDialogSelectField
import com.schoolconnect.app.feature.common.ApiSchoolDropdown
import com.schoolconnect.app.designsystem.AppDialogScaffold
import com.schoolconnect.app.designsystem.AppDialogTextField
import com.schoolconnect.app.designsystem.AppDialogValidationSummary
import com.schoolconnect.app.designsystem.AppDialogErrorMessage
import ischoolconnect.sharedui.generated.resources.Res
import ischoolconnect.sharedui.generated.resources.pencil

private val AcademicYearEditDialogColors = com.schoolconnect.app.designsystem.AppDialogDefaultColors

@Composable
internal fun AcademicYearEditModal(
    draft: AcademicYearEditorDraft,
    schoolOptions: List<String>,
    onDismiss: () -> Unit,
    onSave: () -> Unit,
    onAcademicYearChanged: (String) -> Unit,
    onSchoolChanged: (String) -> Unit,
    onStartDateChanged: (String) -> Unit,
    onEndDateChanged: (String) -> Unit,
    onStatusChanged: (AcademicYearStatus) -> Unit,
    isSaving: Boolean = false,
    apiError: String = ""
) {
    var hasSubmitted by remember { mutableStateOf(true) }
    var schoolExpanded by remember { mutableStateOf(false) }
    var statusExpanded by remember { mutableStateOf(false) }

    val validationErrors = remember(
        hasSubmitted,
        draft.academicYear,
        draft.school,
        draft.startDate,
        draft.endDate,
        draft.status
    ) {
        validateAcademicYearEditDraft(
            academicYear = draft.academicYear,
            school = draft.school,
            startDate = draft.startDate,
            endDate = draft.endDate,
            status = draft.status,
            showErrors = hasSubmitted
        )
    }

    LaunchedEffect(draft.id) {
        hasSubmitted = true
        schoolExpanded = false
        statusExpanded = false
    }

    AppDialogScaffold(
        onDismiss = { if (!isSaving) onDismiss() },
        dismissOnClickOutside = false,
        colors = AcademicYearEditDialogColors,
        isLoading = isSaving,
        loadingTitle = "Saving academic year"
    ) {
        Column(modifier = Modifier.fillMaxWidth()) {
            AppDialogHeader(
                title = "Edit Academic Year",
                subtitle = "Update academic year details and lifecycle.",
                icon = Res.drawable.pencil,
                closeContentDescription = "Close edit academic year modal",
                onClose = { if (!isSaving) onDismiss() },
                colors = AcademicYearEditDialogColors
            )
            AppDialogDivider(colors = AcademicYearEditDialogColors)
            AppDialogValidationSummary(
                errors = listOf(
                    validationErrors.academicYear,
                    validationErrors.school,
                    validationErrors.startDate,
                    validationErrors.endDate,
                    validationErrors.status
                ),
                colors = AcademicYearEditDialogColors,
                modifier = Modifier.padding(horizontal = 28.dp, vertical = 12.dp)
            )
            AppDialogErrorMessage(
                message = apiError,
                colors = AcademicYearEditDialogColors,
                modifier = Modifier.padding(horizontal = 28.dp, vertical = 12.dp)
            )

            AppDialogFormGrid(
                modifier = Modifier
                    .fillMaxWidth()
                    .weight(1f, fill = false),
                compactBreakpoint = 680.dp
            ) { compact ->
                val scrollState = rememberScrollState()
                val columnGap = if (compact) 20.dp else 28.dp
                val fieldGap = if (compact) 20.dp else 24.dp

                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .verticalScroll(scrollState)
                        .padding(horizontal = 28.dp, vertical = 24.dp),
                    verticalArrangement = Arrangement.spacedBy(fieldGap)
                ) {
                    if (compact) {
                        Column(verticalArrangement = Arrangement.spacedBy(fieldGap)) {
                            AcademicYearEditFieldColumn(
                                label = "School",
                                required = true,
                                error = validationErrors.school,
                                field = {
                                    AcademicYearEditSchoolField(
                                        value = draft.school,
                                        expanded = schoolExpanded,
                                        options = schoolOptions.ifEmpty { AcademicYearDefaultSchoolOptions },
                                        onToggleExpanded = { schoolExpanded = !schoolExpanded },
                                        onDismissExpanded = { schoolExpanded = false },
                                        onSelect = {
                                            onSchoolChanged(it)
                                            schoolExpanded = false
                                        }
                                    )
                                }
                            )
                            AcademicYearEditFieldColumn(
                                label = "Start Date",
                                required = true,
                                error = validationErrors.startDate,
                                field = {
                                    AcademicYearEditDateFieldLarge(
                                        value = draft.startDate,
                                        placeholder = "DD MMM YYYY",
                                        onValueChange = onStartDateChanged
                                    )
                                }
                            )
                            AcademicYearEditFieldColumn(
                                label = "End Date",
                                required = true,
                                error = validationErrors.endDate,
                                field = {
                                    AcademicYearEditDateFieldLarge(
                                        value = draft.endDate,
                                        placeholder = "DD MMM YYYY",
                                        onValueChange = onEndDateChanged
                                    )
                                }
                            )
                            AcademicYearEditFieldColumn(
                                label = "Academic Year Label",
                                required = true,
                                error = validationErrors.academicYear,
                                field = {
                                    AcademicYearEditTextField(
                                        value = draft.academicYear,
                                        placeholder = "Enter academic year (e.g. 2026-27)",
                                        onValueChange = onAcademicYearChanged,
                                        readOnly = true
                                    )
                                }
                            )
                            AcademicYearEditFieldColumn(
                                label = "Academic Year Lifecycle",
                                required = true,
                                error = validationErrors.status,
                                field = {
                                    AcademicYearEditStatusField(
                                        value = draft.status,
                                        expanded = statusExpanded,
                                        onToggleExpanded = { statusExpanded = !statusExpanded },
                                        onDismissExpanded = { statusExpanded = false },
                                        onSelect = {
                                            onStatusChanged(it)
                                            statusExpanded = false
                                        }
                                    )
                                }
                            )
                        }
                    } else {
                        Column(verticalArrangement = Arrangement.spacedBy(fieldGap)) {
                            AcademicYearEditFieldColumn(
                                label = "School",
                                required = true,
                                error = validationErrors.school,
                                field = {
                                    AcademicYearEditSchoolField(
                                        value = draft.school,
                                        expanded = schoolExpanded,
                                        options = schoolOptions.ifEmpty { AcademicYearDefaultSchoolOptions },
                                        onToggleExpanded = { schoolExpanded = !schoolExpanded },
                                        onDismissExpanded = { schoolExpanded = false },
                                        onSelect = {
                                            onSchoolChanged(it)
                                            schoolExpanded = false
                                        }
                                    )
                                }
                            )
                            Row(horizontalArrangement = Arrangement.spacedBy(columnGap)) {
                                AcademicYearEditFieldColumn(
                                    modifier = Modifier.weight(1f),
                                    label = "Start Date",
                                    required = true,
                                    error = validationErrors.startDate,
                                    field = {
                                        AcademicYearEditDateFieldLarge(
                                            value = draft.startDate,
                                            placeholder = "DD MMM YYYY",
                                            onValueChange = onStartDateChanged
                                        )
                                    }
                                )
                                AcademicYearEditFieldColumn(
                                    modifier = Modifier.weight(1f),
                                    label = "End Date",
                                    required = true,
                                    error = validationErrors.endDate,
                                    field = {
                                        AcademicYearEditDateFieldLarge(
                                            value = draft.endDate,
                                            placeholder = "DD MMM YYYY",
                                            onValueChange = onEndDateChanged
                                        )
                                    }
                                )
                            }
                            Row(horizontalArrangement = Arrangement.spacedBy(columnGap)) {
                                AcademicYearEditFieldColumn(
                                    modifier = Modifier.weight(1f),
                                    label = "Academic Year Label",
                                    required = true,
                                    error = validationErrors.academicYear,
                                    field = {
                                        AcademicYearEditTextField(
                                            value = draft.academicYear,
                                            placeholder = "Enter academic year (e.g. 2026-27)",
                                            onValueChange = onAcademicYearChanged,
                                            readOnly = true
                                        )
                                    }
                                )
                                AcademicYearEditFieldColumn(
                                    modifier = Modifier.weight(1f),
                                    label = "Academic Year Lifecycle",
                                    required = true,
                                    error = validationErrors.status,
                                    field = {
                                        AcademicYearEditStatusField(
                                            value = draft.status,
                                            expanded = statusExpanded,
                                            onToggleExpanded = { statusExpanded = !statusExpanded },
                                            onDismissExpanded = { statusExpanded = false },
                                            onSelect = {
                                                onStatusChanged(it)
                                                statusExpanded = false
                                            }
                                        )
                                    }
                                )
                            }
                        }
                    }
                }
            }

            AcademicYearEditFooter(
                onCancel = { if (!isSaving) onDismiss() },
                isSaving = isSaving,
                onSave = {
                    hasSubmitted = true
                    val errors = validateAcademicYearEditDraft(
                        academicYear = draft.academicYear,
                        school = draft.school,
                        startDate = draft.startDate,
                        endDate = draft.endDate,
                        status = draft.status,
                        showErrors = true
                    )
                    if (!errors.hasAnyError) {
                        onSave()
                    }
                }
            )
        }
    }
}

@Composable
private fun AcademicYearEditFooter(
    onCancel: () -> Unit,
    onSave: () -> Unit,
    isSaving: Boolean
) {
    AppDialogFooter(
        actions = listOf(
            AppDialogFooterAction(label = "Cancel", onClick = onCancel, enabled = !isSaving),
            AppDialogFooterAction(
                label = if (isSaving) "Saving..." else "Save",
                onClick = onSave,
                style = AppDialogActionStyle.Primary,
                enabled = !isSaving
            )
        ),
        colors = AcademicYearEditDialogColors
    )
}

@Composable
private fun AcademicYearEditFieldColumn(
    modifier: Modifier = Modifier,
    label: String,
    required: Boolean,
    error: String,
    field: @Composable () -> Unit
) {
    AppDialogFieldColumn(
        modifier = modifier,
        label = label,
        required = required,
        error = error,
        colors = AcademicYearEditDialogColors,
        field = field
    )
}

@Composable
private fun AcademicYearEditTextField(
    value: String,
    placeholder: String,
    onValueChange: (String) -> Unit,
    readOnly: Boolean = false
) {
    AppDialogTextField(
        value = value,
        onValueChange = onValueChange,
        placeholder = placeholder,
        colors = AcademicYearEditDialogColors,
        readOnly = readOnly
    )
}

@Composable
private fun AcademicYearEditDateFieldLarge(
    value: String,
    placeholder: String,
    onValueChange: (String) -> Unit
) {
    AppDialogDateField(
        value = value,
        onValueChange = onValueChange,
        placeholder = placeholder,
        colors = AcademicYearEditDialogColors
    )
}

@Composable
private fun AcademicYearEditSchoolField(
    value: String,
    expanded: Boolean,
    options: List<String>,
    onToggleExpanded: () -> Unit,
    onDismissExpanded: () -> Unit,
    onSelect: (String) -> Unit
) {
    ApiSchoolDropdown(
        selectedLabel = value,
        placeholder = "Search and select school",
        onSelected = { onSelect(it.name) }
    )
}

@Composable
private fun AcademicYearEditStatusField(
    value: AcademicYearStatus,
    expanded: Boolean,
    onToggleExpanded: () -> Unit,
    onDismissExpanded: () -> Unit,
    onSelect: (AcademicYearStatus) -> Unit
) {
    AppDialogSelectField(
        value = value.label,
        placeholder = "Select lifecycle",
        expanded = expanded,
        options = AcademicYearStatus.entries.map { it.label },
        onToggleExpanded = onToggleExpanded,
        onDismissExpanded = onDismissExpanded,
        onSelected = { selected ->
            onSelect(AcademicYearStatus.entries.first { it.label == selected })
        },
        colors = AcademicYearEditDialogColors
    )
}

private data class AcademicYearEditFormErrors(
    val academicYear: String = "",
    val school: String = "",
    val startDate: String = "",
    val endDate: String = "",
    val status: String = ""
) {
    val hasAnyError: Boolean
        get() = academicYear.isNotBlank() || school.isNotBlank() || startDate.isNotBlank() ||
            endDate.isNotBlank() || status.isNotBlank()
}

private fun validateAcademicYearEditDraft(
    academicYear: String,
    school: String,
    startDate: String,
    endDate: String,
    status: AcademicYearStatus,
    showErrors: Boolean
): AcademicYearEditFormErrors {
    if (!showErrors) return AcademicYearEditFormErrors()

    val academicYearError = if (academicYear.isBlank()) "Academic year label is required." else ""
    val schoolError = if (school.isBlank()) "School is required." else ""
    val startDateError = if (startDate.isBlank()) "Start date is required." else ""
    val endDateError = if (endDate.isBlank()) "End date is required." else ""
    val statusError = if (status.label.isBlank()) "Academic year lifecycle is required." else ""
    val rangeError = if (
        startDateError.isBlank() &&
        endDateError.isBlank() &&
        !isValidDateRange(startDate, endDate)
    ) {
        "End date must be on or after start date."
    } else {
        ""
    }

    return AcademicYearEditFormErrors(
        academicYear = academicYearError,
        school = schoolError,
        startDate = if (rangeError.isNotBlank()) rangeError else startDateError,
        endDate = if (rangeError.isNotBlank()) rangeError else endDateError,
        status = statusError
    )
}

private fun isValidDateRange(startDate: String, endDate: String): Boolean {
    val start = appDateInputComparable(startDate) ?: return false
    val end = appDateInputComparable(endDate) ?: return false
    return start <= end
}
